import React, { useState } from 'react';
import { Button } from 'equasion-lib';
import { FiMinus, FiPlus } from 'react-icons/fi';
import {
  parseFoundQuantity,
  pickConfirmedStopAt,
  pickGrabShortfall,
  pickNextStopNote,
  pickShortPrompt,
  pickZoneGrabStops,
  pickZoneShare,
  pickZoneShortPrompt,
  type PickLine,
  type PickWalkStop
} from '../../pickLineSteps';

type PickGrabPanelProps = {
  line: PickLine;
  /** The zone the unit was just confirmed at. */
  zoneId?: string | null;
  /** The whole walk, to say where the line's other zones are. */
  stops?: readonly PickWalkStop[] | null;
  /** The short is in flight. */
  busy: boolean;
  shortEntryOpen: boolean;
  onDone: () => void;
  onOpenShortEntry: () => void;
  onCloseShortEntry: () => void;
  onSubmitShort: (foundQuantity: number) => void;
};

const click = (onClick: () => void) => ({ onClick });

/**
 * Step 3: the one unit matched, so the picker now grabs THIS ZONE's share unscanned — down every
 * lot of the zone the allocation named — and either confirms (Done; the zone is already recorded)
 * or says how many the zone actually had (Couldn't find them all here → the zone records that, and
 * the rest moves to the line's other zones, or the line goes short when none has it). The line's
 * other zones are stops of their own further along the walk: the panel says where, and the picker
 * scans them there. Nothing is scanned on this step.
 *
 * From an older server without per-zone stops (no `confirmedStops`), the line's every stop is
 * grabbed here and the short is the whole line's, as before.
 */
function PickGrabPanel({
  line,
  zoneId = null,
  stops = null,
  busy,
  shortEntryOpen,
  onDone,
  onOpenShortEntry,
  onCloseShortEntry,
  onSubmitShort
}: PickGrabPanelProps) {
  const [found, setFound] = useState('');
  const perZone =
    Array.isArray(line.confirmedStops) && !!pickConfirmedStopAt(line, zoneId);
  const grabStops = pickZoneGrabStops(line, zoneId);
  const share = perZone ? pickZoneShare(line, zoneId) : line.quantity;
  const zoneName = perZone
    ? pickConfirmedStopAt(line, zoneId)?.zone?.name || 'this zone'
    : grabStops[0]?.zoneName || 'this zone';
  // The line's last zone, with less recorded stock than the line needs: the line is `picked` on
  // what the records hold, and finding exactly the zone's share is then a short of the LINE.
  const picked = line.pickedQuantity ?? line.quantity;
  const lineShortOfStock = perZone && line.status === 'picked' && picked < line.quantity;
  // At a zone the unit just scanned is one found: at least 1, fewer than the share (up to the share
  // when the line is short of stock). The whole-line short of an older server may record none.
  const minFound = perZone ? 1 : 0;
  const maxFound = lineShortOfStock ? share : share - 1;
  const foundQuantity = parseFoundQuantity(found, maxFound + 1, minFound);
  // One unit to take here, one unit scanned: there is nothing that could be missing.
  const canBeShort = share > 1 || lineShortOfStock;
  const next = perZone ? pickNextStopNote(line, stops) : null;
  const shortfall = perZone
    ? lineShortOfStock
      ? line.quantity - picked
      : 0
    : pickGrabShortfall(line);
  const step = (delta: number) => {
    const current = parseFoundQuantity(found, maxFound + 1, minFound);
    const value =
      current === null ? (delta > 0 ? Math.max(1, minFound) : minFound) : current + delta;
    setFound(String(Math.min(Math.max(value, minFound), maxFound)));
  };

  if (shortEntryOpen) {
    return (
      <div className="pick-grab pick-grab--short">
        <h2>Couldn&apos;t find them all{perZone ? ' here' : ''}</h2>
        <p>{perZone ? pickZoneShortPrompt(zoneName, share) : pickShortPrompt(line)}</p>
        <div className="pick-grab__stepper">
          <button
            type="button"
            aria-label="One fewer"
            onClick={() => step(-1)}
            disabled={busy}
          >
            <FiMinus />
          </button>
          <input
            aria-label="Units found"
            name="foundQuantity"
            inputMode="numeric"
            autoComplete="off"
            value={found}
            onChange={(e) => setFound(e.target.value.replace(/\D/g, ''))}
          />
          <button
            type="button"
            aria-label="One more"
            onClick={() => step(1)}
            disabled={busy}
          >
            <FiPlus />
          </button>
        </div>
        <p className="pick-grab__note">
          {lineShortOfStock
            ? `Found ${
                foundQuantity ?? '—'
              } in ${zoneName}. No other zone has the rest — the line is marked short, and the rest is cancelled when Pick is completed.`
            : perZone
            ? `Found ${
                foundQuantity ?? '—'
              } of ${share} in ${zoneName}. The rest is looked for in the line's other zones; if none has it, the line is marked short.`
            : `Found ${foundQuantity ?? '—'} of ${
                line.quantity
              }. The rest is cancelled when Pick is completed.`}
        </p>
        <div className="pick-grab__actions">
          <Button
            type="primary"
            fullWidth
            disabled={busy || foundQuantity === null}
            {...click(() => {
              if (foundQuantity !== null) onSubmitShort(foundQuantity);
            })}
          >
            {busy ? 'Saving…' : perZone ? 'Record what was here' : 'Record short'}
          </Button>
          <Button
            type="secondary"
            fullWidth
            disabled={busy}
            {...click(onCloseShortEntry)}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pick-grab">
      <h2>{perZone && next ? `Grab the rest from ${zoneName}` : 'Grab the rest'}</h2>
      {grabStops.length > 0 ? (
        <ol className="pick-grab__stops">
          {grabStops.map((stop, index) => (
            <li key={`${stop.zoneName}-${stop.lot ?? ''}-${index}`}>
              <span className="pick-grab__count">
                <small>Qty</small>
                {stop.quantity}
              </span>
              <span className="pick-grab__where">
                <span>
                  Zone: <strong>{stop.zoneName}</strong>
                </span>
                {(stop.expiration || stop.lot) && (
                  <span className="pick-grab__meta">
                    {stop.expiration && <>Exp {stop.expiration}</>}
                    {stop.expiration && stop.lot && ' · '}
                    {stop.lot && <>Lot {stop.lot}</>}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <div className="pick-grab__callout">
          <span className="pick-grab__count">
            <small>Qty</small>
            {share}
          </span>
          <span className="pick-grab__where">{share === 1 ? 'unit' : 'units'}</span>
        </div>
      )}
      {next && (
        <p
          className={[
            'pick-grab__note',
            next.kind === 'noStock' ? 'pick-grab__note--warn' : 'pick-grab__note--next'
          ].join(' ')}
        >
          {next.text}
        </p>
      )}
      {shortfall > 0 && (
        <p className="pick-grab__note pick-grab__note--warn">
          Only {line.quantity - shortfall} of {line.quantity} are recorded in stock. If
          you cannot find the rest, tap Couldn&apos;t find them all
          {perZone ? ' here' : ''}.
        </p>
      )}
      <div className="pick-grab__actions">
        <Button type="primary" fullWidth disabled={busy} {...click(onDone)}>
          Done
        </Button>
        {canBeShort && (
          <Button type="secondary" fullWidth disabled={busy} {...click(onOpenShortEntry)}>
            Couldn&apos;t find them all{perZone ? ' here' : ''}
          </Button>
        )}
      </div>
    </div>
  );
}

export default PickGrabPanel;
